import { Seo } from "../components/Seo";
import React from "react";
import { useContent } from "../context/ContentContext";
import { resolveImg } from "../lib/api";
import { PageHero } from "../components/PageHero";
import { Reveal, Overline } from "../components/Reveal";
import { ValuesMarquee } from "../components/ValuesMarquee";
import { Pikorua } from "../components/Pikorua";
import { VALUES } from "../data/defaults";

const Chapter = ({ n, title, body, delay }) => (
  <Reveal delay={delay}>
    <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start py-10 border-t border-navy/10">
      <div className="md:col-span-3 font-heading text-6xl md:text-7xl text-gold font-medium leading-none">{n}</div>
      <div className="md:col-span-9">
        <h3 className="font-heading text-2xl md:text-3xl text-navy font-medium">{title}</h3>
        <p className="mt-4 text-navy/70 text-lg leading-relaxed max-w-2xl">{body}</p>
      </div>
    </div>
  </Reveal>
);

export default function About() {
  const { content } = useContent();
  return (
    <div data-testid="about-page">
      <Seo title="About Us — Te Manawa Table Tennis Association" description="Why Te Manawa was formed, our purpose and values, governance with Table Tennis New Zealand, and the board leading table tennis across the central North Island." />
      <PageHero overline="About Us" title={content.about_hero_heading} subtitle={content.about_hero_sub} />

      {/* Image + purpose */}
      <section className="bg-cream py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              {content.about_image && <img src={resolveImg(content.about_image)} alt="Te Manawa community" className="w-full aspect-[4/3] object-cover" />}
              <div className="absolute -bottom-5 -right-5 h-24 w-24 bg-gold hidden sm:block" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <Overline className="text-navy/60">Our purpose</Overline>
            <p className="font-heading text-2xl md:text-3xl text-navy font-medium tracking-tight leading-snug mt-5">
              {content.purpose_body}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Formation story */}
      <section className="bg-white py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-8">
            <Overline className="text-navy/60">Why we were formed</Overline>
            <h2 className="font-heading text-4xl sm:text-5xl text-navy font-medium tracking-tight mt-5">Our story</h2>
          </Reveal>
          <Chapter n="01" title={content.story_1_title} body={content.story_1_body} delay={0} />
          <Chapter n="02" title={content.story_2_title} body={content.story_2_body} delay={0.1} />
          <Chapter n="03" title={content.story_3_title} body={content.story_3_body} delay={0.2} />
        </div>
      </section>

      <ValuesMarquee light />

      {/* Values */}
      <section className="bg-cream py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <Overline className="text-navy/60">Our values</Overline>
            <h2 className="font-heading text-4xl sm:text-5xl text-navy font-medium tracking-tight mt-5">What guides us</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v, i) => (
              <Reveal key={v.name} delay={i * 0.08}>
                <div className="h-full bg-white border border-navy/10 p-8 transition-colors hover:border-gold">
                  <span className="font-heading text-gold text-sm font-semibold">0{i + 1}</span>
                  <h3 className="font-heading text-2xl text-navy font-medium mt-2">{v.name}</h3>
                  <p className="mt-3 text-navy/70 leading-relaxed text-sm">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Governance + Board */}
      <section className="relative bg-navy-dark text-white overflow-hidden py-24 md:py-28">
        <div className="absolute inset-0 noise-overlay" />
        <Pikorua className="absolute -left-16 bottom-0 w-80 h-80 text-gold opacity-[0.06]" stroke="#F2A900" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12">
          <Reveal>
            <Overline className="text-gold">Governance</Overline>
            <h3 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-5">Affiliated with Table Tennis New Zealand</h3>
            <p className="mt-5 text-white/70 leading-relaxed text-lg">{content.governance_body}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <Overline className="text-gold">Board & leadership</Overline>
            <h3 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-5">Led by our community</h3>
            <p className="mt-5 text-white/70 leading-relaxed text-lg">{content.board_body}</p>
          </Reveal>
        </div>
      </section>

      {/* Pikorua symbolism */}
      <section className="bg-cream py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <div className="flex justify-center mb-8">
              <Pikorua className="w-24 h-24 text-navy" stroke="#0A192F" />
            </div>
            <Overline className="text-navy/60">Pikorua · the triple twist</Overline>
            <p className="font-heading text-2xl md:text-3xl text-navy font-medium tracking-tight leading-snug mt-6">
              {content.pikorua_body}
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
